import React from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Tag,
  Select,
  Space,
  Typography,
  Divider,
} from 'antd';
import { useAuthStore } from '../store/auth';
import { BoxIntegration } from '../components/Integrations';
import { ModelTesting } from '../components/Settings/ModelTesting';
import { useTheme } from '../theme/enhanced-theme-provider';
import { getAvailableThemeSelections, getThemeDisplayName } from '../theme/theme-resolver';
import { ThemeSelection, ThemeMode } from '../theme/types';

const { Text } = Typography;

const SettingsPage: React.FC = () => {
  const { user } = useAuthStore();
  const { 
    currentTheme, 
    themeSelection, 
    themeMode, 
    setTheme, 
    setThemeMode, 
    saveThemeSettings 
  } = useTheme();

  const availableThemeSelections = getAvailableThemeSelections();
  const availableThemeModes: { value: ThemeMode; label: string; description: string }[] = [
    { value: 'light', label: 'Light', description: 'Light color scheme' },
    { value: 'dark', label: 'Dark', description: 'Dark color scheme' },
    { value: 'high-contrast', label: 'High Contrast', description: 'High contrast for accessibility' },
    { value: 'auto', label: 'Auto', description: 'Follow system preference' },
  ];

  const handleThemeSelectionChange = async (selection: ThemeSelection) => {
    setTheme(selection);
    await saveThemeSettings();
  };

  const handleThemeModeChange = async (mode: ThemeMode) => {
    setThemeMode(mode);
    await saveThemeSettings();
  };

  const userRows = [
    { id: '1', property: 'User ID', value: user?.id || 'N/A' },
    { id: '2', property: 'Email', value: user?.email || 'N/A' },
    { id: '3', property: 'Name', value: user?.name || 'N/A' },
    { id: '4', property: 'Company Name', value: user?.companyName || 'N/A' },
    { id: '5', property: 'User Principal Name', value: user?.userPrincipalName || 'N/A' },
    { id: '6', property: 'Job Title', value: user?.jobTitle || 'N/A' },
    { id: '7', property: 'Department', value: user?.department || 'N/A' },
    { id: '8', property: 'Office Location', value: user?.officeLocation || 'N/A' },
    { id: '9', property: 'Country', value: user?.country || 'N/A' },
    { id: '10', property: 'City', value: user?.city || 'N/A' },
    { id: '11', property: 'Preferred Language', value: user?.preferredLanguage || 'N/A' },
  ].filter(row => row.value !== 'N/A'); // Only show rows with actual values

  const userHeaders = [
    { key: 'property', header: 'Property' },
    { key: 'value', header: 'Value' },
  ];

  return (
    <div className="settings-page">
      <Row>
        <Col span={16}>
        <h1 style={{ marginBottom: '2rem' }}>Settings</h1>

        <Card style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>User Information</h4>
          <Table
            dataSource={userRows}
            columns={[
              {
                title: 'Property',
                dataIndex: 'property',
                key: 'property',
              },
              {
                title: 'Value',
                dataIndex: 'value',
                key: 'value',
              },
            ]}
            pagination={false}
            size="small"
          />
        </Card>

        <Card style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Roles</h4>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {user?.roles.map((role) => (
              <Tag key={role} color="blue">
                {role}
              </Tag>
            )) || <span>No roles assigned</span>}
          </div>
        </Card>

        <Card style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Permissions</h4>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {user?.permissions.map((permission) => (
              <Tag key={permission} color="green">
                {permission}
              </Tag>
            )) || <span>No permissions assigned</span>}
          </div>
        </Card>

        <Card style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Theme Settings</h4>
          
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12}>
                <div>
                  <Text strong>Theme Selection</Text>
                  <div style={{ marginTop: '0.5rem' }}>
                    <Select
                      value={themeSelection}
                      onChange={handleThemeSelectionChange}
                      style={{ width: '100%' }}
                      placeholder="Select theme"
                      size="large"
                    >
                      {availableThemeSelections.map(theme => (
                        <Select.Option key={theme.id} value={theme.id}>
                          {theme.name}
                        </Select.Option>
                      ))}
                    </Select>
                    {themeSelection && (
                      <div style={{ 
                        marginTop: '0.5rem', 
                        fontSize: '13px', 
                        color: 'var(--color-text-secondary)',
                        lineHeight: '1.4'
                      }}>
                        {availableThemeSelections.find(t => t.id === themeSelection)?.description}
                      </div>
                    )}
                  </div>
                  {themeSelection === 'auto' && (
                    <div style={{ marginTop: '0.5rem', fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                      Current resolved theme: <strong>{currentTheme.name}</strong>
                    </div>
                  )}
                </div>
              </Col>
              
              <Col xs={24} sm={24} md={12}>
                <div>
                  <Text strong>Color Mode</Text>
                  <div style={{ marginTop: '0.5rem' }}>
                    <Select
                      value={themeMode}
                      onChange={handleThemeModeChange}
                      style={{ width: '100%' }}
                      placeholder="Select color mode"
                      size="large"
                    >
                      {availableThemeModes.map(mode => (
                        <Select.Option key={mode.value} value={mode.value}>
                          {mode.label}
                        </Select.Option>
                      ))}
                    </Select>
                    {themeMode && (
                      <div style={{ 
                        marginTop: '0.5rem', 
                        fontSize: '13px', 
                        color: 'var(--color-text-secondary)',
                        lineHeight: '1.4'
                      }}>
                        {availableThemeModes.find(m => m.value === themeMode)?.description}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{ 
              padding: '1rem', 
              backgroundColor: 'var(--color-bg-secondary)', 
              borderRadius: '8px',
              border: '1px solid var(--color-border-secondary)'
            }}>
              <Text strong>Current Theme Preview</Text>
              <div style={{ marginTop: '0.5rem' }}>
                <Space wrap>
                  <div style={{ 
                    padding: '8px 16px', 
                    backgroundColor: 'var(--color-primary)', 
                    color: 'white', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    Primary
                  </div>
                  <div style={{ 
                    padding: '8px 16px', 
                    backgroundColor: 'var(--color-success)', 
                    color: 'white', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    Success
                  </div>
                  <div style={{ 
                    padding: '8px 16px', 
                    backgroundColor: 'var(--color-warning)', 
                    color: 'white', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    Warning
                  </div>
                  <div style={{ 
                    padding: '8px 16px', 
                    border: '1px solid var(--color-border-primary)', 
                    color: 'var(--color-text-primary)', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    Border Sample
                  </div>
                </Space>
              </div>
            </div>
          </Space>
        </Card>

        <ModelTesting />

        <BoxIntegration style={{ marginBottom: '2rem' }} />
        </Col>
      </Row>
    </div>
  );
};

export default SettingsPage;